import { useState } from 'react';
import { useStore } from 'react-redux';
import type { RootState } from '@/store';
import { rotaDaAssinaturaDoAdmin } from '@/shared/constants/rotasFluxos';
import { useToast } from '@/shared/providers';
import { registrarAcao } from '@/shared/utils/reportAction';
import { reportError } from '@/shared/utils/reportError';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { enviarTextoDaSimulacao, buscarEventosDaSimulacao, exportarEventosDaSimulacao, executarEntrada, responderInteracaoDaSimulacao, tentarNovamenteEntrada } from '../store/actions';
import { selectConfiguracaoDoAdmin, selectEnvioDaConversa, selectEventosParaExportacao, selectEventosVisiveis, selectLimpoAteSeq, selectSimulacaoAtual, selectPaginacaoDaSimulacao } from '../store/selectors';
import { conversaLimpa } from '../store/slice';
import type { ConfiguracaoDoAdmin, ContextoDaExportacao, EstadoSimulacao, FormatoDaExportacao, RotaDaAssinatura, SimulacaoReply } from '../types';
import { podeTentarDeNovo, rascunhoAposFalha, statusDoCompositor, textoValidoParaEnvio } from '../utils/compositorDaSimulacao';
import { downloadConversationMd, downloadConversationPdf } from '../utils/conversationExport';
import { ehEstadoDoAdmin, linhasDoAdminParaExportacao } from '../utils/execucaoDoAdmin';
import { mensagensDosEventos, mensagensRespondidas, rotaDaAssinaturaDoEscritorio, situacaoDaConversa } from '../utils/mensagensDaConversa';
import { ROTULO_DA_SITUACAO } from '../utils/rotulosDaSimulacao';
const MENSAGEM_DA_FALHA_NA_EXPORTACAO = 'Não foi possível baixar a conversa. Tente novamente.';

/**
 * O card de assinatura leva à tela da superfície da conversa, com o contexto dela (CA-27). Sem
 * fluxo inicial (apagado depois do início), o segmento vai vazio, como no link que o core monta
 * (`rotaDaTelaDeAssinatura`).
 */
const rotaDaAssinaturaDaConversa = (simulacao: EstadoSimulacao | null): RotaDaAssinatura => ehEstadoDoAdmin(simulacao) ? assinaturaId => rotaDaAssinaturaDoAdmin(simulacao.fluxoInicialId ?? '', assinaturaId, simulacao.escritorioId) : rotaDaAssinaturaDoEscritorio;
const contextoDaExportacao = (simulacao: EstadoSimulacao, configuracao: ConfiguracaoDoAdmin | null, base: Omit<ContextoDaExportacao, 'linhasDoAdmin'>): ContextoDaExportacao => ehEstadoDoAdmin(simulacao) ? {
  ...base,
  linhasDoAdmin: linhasDoAdminParaExportacao(simulacao, configuracao)
} : base;

/**
 * A conversa vem só dos eventos do core (CA-8): nada de resposta montada no navegador. O rascunho
 * é estado da tela: sai do campo no envio e volta na falha (CA-9).
 */
export function useConversaDaSimulacao(agentName: string) {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const toast = useToast();
  const simulacao = useAppSelector(selectSimulacaoAtual);
  const eventosVisiveis = useAppSelector(selectEventosVisiveis);
  const todosOsEventos = useAppSelector(selectEventosParaExportacao);
  const limpoAteSeq = useAppSelector(selectLimpoAteSeq);
  const envio = useAppSelector(selectEnvioDaConversa);
  const paginacao = useAppSelector(selectPaginacaoDaSimulacao);
  const configuracaoDoAdmin = useAppSelector(selectConfiguracaoDoAdmin).dados;
  const [draft, setDraft] = useState('');
  const [exportError, setExportError] = useState<string | null>(null);
  const ativa = simulacao?.status === 'ATIVA';
  const sending = envio?.status === 'enviando';

  /**
   * O campo esvazia no envio, como no WhatsApp: esperar o core deixava o texto parado ali durante
   * toda a resposta da IA. Na falha o rascunho volta (CA-9) pela regra de `rascunhoAposFalha`.
   */
  const enviar = async () => {
    if (!ativa || sending || !textoValidoParaEnvio(draft)) return;
    const rascunho = draft;
    setDraft('');
    const resultado = await dispatch(enviarTextoDaSimulacao(rascunho.trim()));
    if (executarEntrada.fulfilled.match(resultado)) return;
    const falha = selectEnvioDaConversa(store.getState());
    const podeRepetir = falha?.status === 'falhou' && podeTentarDeNovo(falha);
    setDraft(noCampo => rascunhoAposFalha(noCampo, rascunho, podeRepetir));
  };
  const responder = ({
    messageId,
    optionId
  }: SimulacaoReply) => {
    void dispatch(responderInteracaoDaSimulacao(messageId, optionId));
  };
  const tentarNovamente = () => {
    void dispatch(tentarNovamenteEntrada());
  };
  const limpar = () => {
    dispatch(conversaLimpa());
    setExportError(null);
  };

  /**
   * Leva todos os eventos, inclusive os que o Limpar escondeu (CA-21, CA-22). Sem evento não há
   * arquivo: um histórico vazio não gera documento enganoso. A falha só mostra o aviso; a conversa
   * fica como estava.
   */
  const baixar = async (formato: FormatoDaExportacao, arquivosIndisponiveis: string[]) => {
    if (!simulacao || paginacao.exportacao.carregando) return;
    const escritorioDaExportacao = store.getState().negociacaoSimulacao.escritorioId;
    const contexto = contextoDaExportacao(simulacao, configuracaoDoAdmin, {
      agenteNome: agentName,
      arquivosIndisponiveis
    });
    const telemetria = {
      simulacaoId: simulacao?.id ?? null,
      formato,
      eventos: todosOsEventos.length
    };
    try {
      setExportError(null);
      const resultado_0 = await dispatch(exportarEventosDaSimulacao(simulacao.id));
      if (!exportarEventosDaSimulacao.fulfilled.match(resultado_0)) throw new Error(MENSAGEM_DA_FALHA_NA_EXPORTACAO);
      const estadoAtual = store.getState();
      const simulacaoAtual = estadoAtual.negociacaoSimulacao;
      if (simulacaoAtual.simulacao?.id !== simulacao.id || simulacaoAtual.escritorioId !== escritorioDaExportacao) return;
      const eventos = selectEventosParaExportacao(estadoAtual).filter(evento => evento.seq <= simulacaoAtual.exportacao.total);
      if (eventos.length === 0) return;
      if (formato === 'md') downloadConversationMd(eventos, contexto);else await downloadConversationPdf(eventos, contexto);
      registrarAcao('negociacaoSimulacao.conversaExportada', {
        ...telemetria,
        eventos: eventos.length
      });
    } catch (erro) {
      reportError(erro instanceof Error ? erro : new Error('Falha ao gerar a exportação'), {
        source: 'negociacaoSimulacao',
        acao: 'exportarConversa',
        ...telemetria
      });
      // O aviso do mock fica no painel; o toast é o aviso que o CA-21 pede.
      setExportError(MENSAGEM_DA_FALHA_NA_EXPORTACAO);
      toast.showError(MENSAGEM_DA_FALHA_NA_EXPORTACAO);
    }
  };
  return {
    started: simulacao !== null,
    situation: simulacao ? ROTULO_DA_SITUACAO[situacaoDaConversa(simulacao, todosOsEventos)] : null,
    messages: mensagensDosEventos(eventosVisiveis, rotaDaAssinaturaDaConversa(simulacao)),
    cleared: limpoAteSeq > 0,
    hasMessages: todosOsEventos.length > 0,
    hasVisibleMessages: eventosVisiveis.length > 0,
    answeredMessageIds: mensagensRespondidas(todosOsEventos),
    activeInteractionId: ativa && !sending ? simulacao.interacaoAtivaMensagemId ?? null : null,
    inputEnabled: ativa,
    attachEnabled: ativa && simulacao.aceitaAnexo,
    sending,
    typing: ativa && simulacao.processando,
    composerStatus: statusDoCompositor(simulacao, envio),
    draft,
    setDraft,
    enviar,
    responder,
    tentarNovamente,
    limpar,
    baixar,
    exportError,
    paginacao,
    carregarMais: () => {
      if (simulacao && !paginacao.carregando) void dispatch(buscarEventosDaSimulacao({
        simulacaoId: simulacao.id,
        aposSeq: paginacao.ultimoSeq
      }));
    }
  };
}